import { apiClient, type Paginated } from '@/core/api';
import type { PayrollRun, PayrollRunInput, Payslip, PayslipInput } from './types';

export const BASE = '/finance/payroll-runs/';
const SLIPS = '/finance/payslips/';

export interface RunListParams {
  page?: number;
  status?: string;
  /** DRF `OrderingFilter` value, e.g. `period_start` or `-finalized_at`. */
  ordering?: string;
}

export async function listRuns(params: RunListParams = {}): Promise<Paginated<PayrollRun>> {
  const { data } = await apiClient.get<Paginated<PayrollRun>>(BASE, { params });
  return data;
}

export async function getRun(id: string | number): Promise<PayrollRun> {
  const { data } = await apiClient.get<PayrollRun>(`${BASE}${id}/`);
  return data;
}

export async function createRun(input: PayrollRunInput): Promise<PayrollRun> {
  const { data } = await apiClient.post<PayrollRun>(BASE, input);
  return data;
}

export async function updateRun(
  id: string | number,
  input: Partial<PayrollRunInput>,
): Promise<PayrollRun> {
  const { data } = await apiClient.patch<PayrollRun>(`${BASE}${id}/`, input);
  return data;
}

export async function deleteRun(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

/** POST /finance/payroll-runs/{id}/finalize/ → draft → finalized. */
export async function finalizeRun(id: string | number): Promise<PayrollRun> {
  const { data } = await apiClient.post<PayrollRun>(`${BASE}${id}/finalize/`, {});
  return data;
}

/** PayrollRun + the count of payslips just generated from staff invoices. */
export interface GenerateFromInvoicesResult extends PayrollRun {
  payslips_created: number;
}

/**
 * POST /finance/payroll-runs/{id}/generate-from-invoices/ — build payslips from
 * each employee's signed staff invoice for the period (ADR-049). Idempotent.
 */
export async function generateFromInvoices(
  id: string | number,
): Promise<GenerateFromInvoicesResult> {
  const { data } = await apiClient.post<GenerateFromInvoicesResult>(
    `${BASE}${id}/generate-from-invoices/`,
    {},
  );
  return data;
}

export async function listPayslips(payrollRun: number): Promise<Paginated<Payslip>> {
  const { data } = await apiClient.get<Paginated<Payslip>>(SLIPS, {
    params: { payroll_run: payrollRun },
  });
  return data;
}

export async function createPayslip(input: PayslipInput): Promise<Payslip> {
  const { data } = await apiClient.post<Payslip>(SLIPS, input);
  return data;
}
